import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CodeBracketIcon, LockClosedIcon, BoltIcon,
  CircleStackIcon, ExclamationTriangleIcon, CheckCircleIcon,
  ChevronDownIcon, ChevronUpIcon,
} from '@heroicons/react/24/outline';

const BASE = 'https://wits-api-55ec437f121b.herokuapp.com';

/* ── tiny components ─────────────────────────────────────────────── */

function Method({ m }: { m: 'GET' | 'POST' | 'PUT' | 'DELETE' }) {
  const cls: Record<string, string> = {
    GET:    'bg-emerald-500/10 border border-emerald-500/25 text-emerald-400',
    POST:   'bg-sky-500/10    border border-sky-500/25    text-sky-400',
    PUT:    'bg-amber-500/10  border border-amber-500/25  text-amber-400',
    DELETE: 'bg-red-500/10    border border-red-500/25    text-red-400',
  };
  return (
    <span className={`inline-block text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded ${cls[m]}`}>
      {m}
    </span>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <pre className="rounded-xl border border-white/8 bg-black/40 text-white/60 text-xs font-mono p-4 overflow-x-auto whitespace-pre leading-relaxed">
      {children}
    </pre>
  );
}

function Badge({ children, color = 'green' }: { children: React.ReactNode; color?: string }) {
  const cls = color === 'red'   ? 'bg-red-500/10 border-red-500/20 text-red-400'
            : color === 'amber' ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
            : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider border ${cls}`}>
      {children}
    </span>
  );
}

interface EndpointProps {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  path: string;
  summary: string;
  auth?: 'none' | 'api-key';
  params?: { name: string; in: string; desc: string; required?: boolean }[];
  response: string;
  example?: string;
}

function Endpoint({ method, path, summary, auth = 'api-key', params, response, example }: EndpointProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/3 transition-colors text-left"
      >
        <Method m={method} />
        <code className="flex-1 text-white/70 text-xs font-mono">{path}</code>
        {auth === 'none' && <Badge color="green">Public</Badge>}
        {auth === 'api-key' && <Badge color="amber">API Key</Badge>}
        {open ? <ChevronUpIcon className="w-4 h-4 text-white/25 shrink-0" /> : <ChevronDownIcon className="w-4 h-4 text-white/25 shrink-0" />}
      </button>
      {open && (
        <div className="border-t border-white/6 px-4 py-4 flex flex-col gap-4">
          <p className="text-white/55 text-sm">{summary}</p>
          {params && params.length > 0 && (
            <div>
              <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Parameters</p>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-white/20 text-[10px] uppercase tracking-wider text-left">
                    <th className="pb-1 pr-4 font-medium">Name</th>
                    <th className="pb-1 pr-4 font-medium">In</th>
                    <th className="pb-1 pr-4 font-medium">Required</th>
                    <th className="pb-1 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {params.map((p) => (
                    <tr key={p.name} className="border-t border-white/4">
                      <td className="py-1.5 pr-4 font-mono text-green-400/80">{p.name}</td>
                      <td className="py-1.5 pr-4 text-white/35">{p.in}</td>
                      <td className="py-1.5 pr-4">{p.required ? <span className="text-amber-400 text-[10px]">required</span> : <span className="text-white/20 text-[10px]">optional</span>}</td>
                      <td className="py-1.5 text-white/40">{p.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div>
            <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Response</p>
            <Code>{response}</Code>
          </div>
          {example && (
            <div>
              <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Example request</p>
              <Code>{example}</Code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────── */

export default function ApiDocs() {
  const sections = [
    'Overview', 'Authentication', 'Rate Limits', 'Endpoints',
    'Response Format', 'Error Codes', 'Code Examples',
  ];
  const [activeSection, setActiveSection] = useState('Overview');

  function scrollTo(id: string) {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div className="min-h-screen" style={{ background: '#030d07', color: '#fff' }}>

      {/* Nav bar */}
      <nav className="sticky top-0 z-40 border-b border-white/6 px-6 py-3 flex items-center justify-between" style={{ background: 'rgba(3,13,7,0.95)', backdropFilter: 'blur(12px)' }}>
        <Link to="/" className="flex items-center gap-2">
          <span className="text-green-400 text-sm font-bold tracking-wider" style={{ fontFamily: 'var(--font-display)' }}>WITS</span>
          <span className="text-white/25 text-xs">/ API Docs</span>
        </Link>
        <div className="flex items-center gap-4">
          <span className="text-white/25 text-xs">v1.0</span>
          <Link to="/accounts/login" className="px-3 py-1.5 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-semibold transition-colors">
            Get API Key
          </Link>
        </div>
      </nav>

      <div className="flex max-w-6xl mx-auto">

        {/* Sidebar */}
        <aside className="hidden lg:flex flex-col w-52 shrink-0 sticky top-14 self-start h-[calc(100vh-3.5rem)] overflow-y-auto py-8 pr-4 pl-4">
          <p className="text-white/20 text-[9px] uppercase tracking-widest mb-3">Contents</p>
          {sections.map((s) => (
            <button
              key={s}
              onClick={() => scrollTo(s)}
              className={`text-left py-1.5 px-2.5 rounded-lg text-xs transition-colors ${
                activeSection === s
                  ? 'text-green-400 bg-green-500/8'
                  : 'text-white/35 hover:text-white/60 hover:bg-white/4'
              }`}
            >
              {s}
            </button>
          ))}
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 py-10 px-6 flex flex-col gap-12">

          {/* Hero */}
          <div>
            <p className="text-green-400 text-xs uppercase tracking-[0.2em] font-semibold mb-2">WITS Platform</p>
            <h1 className="text-white/90 text-3xl font-bold mb-3" style={{ fontFamily: 'var(--font-display)' }}>
              Public API Reference
            </h1>
            <p className="text-white/45 text-sm leading-relaxed max-w-2xl">
              The WITS API gives organisations programmatic access to wildlife crime intelligence data —
              seizure cases, species statistics, geographic hotspots, and case precedents.
              Use it to power dashboards, research tools, and conservation decision platforms.
            </p>
          </div>

          {/* ── Overview ── */}
          <section id="Overview">
            <SectionHead icon={<CircleStackIcon className="w-5 h-5" />} title="Overview" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              {[
                { label: 'Base URL',    value: BASE },
                { label: 'Protocol',   value: 'HTTPS only' },
                { label: 'Format',     value: 'JSON (UTF-8)' },
              ].map((i) => (
                <div key={i.label} className="rounded-xl border border-white/6 px-4 py-3" style={{ background: '#0a2e18' }}>
                  <p className="text-white/25 text-[10px] uppercase tracking-wider mb-1">{i.label}</p>
                  <p className="text-white/70 text-xs font-mono">{i.value}</p>
                </div>
              ))}
            </div>
            <p className="text-white/40 text-sm leading-relaxed">
              All public data endpoints (cases, summary, precedents) are available without authentication for read-only access.
              Organisation API keys unlock identity-tracked access with dedicated rate limits and are required for any write operations.
            </p>
          </section>

          {/* ── Authentication ── */}
          <section id="Authentication">
            <SectionHead icon={<LockClosedIcon className="w-5 h-5" />} title="Authentication" />
            <p className="text-white/40 text-sm leading-relaxed mb-4">
              Pass your API key in the <code className="text-green-400/80 bg-white/5 px-1.5 py-0.5 rounded text-xs">X-Api-Key</code> header
              on every request that requires authentication. You can create and manage keys from your{' '}
              <Link to="/accounts/login" className="text-green-400 hover:underline">WITS dashboard → API Keys</Link>.
            </p>
            <Code>{`# Authenticated request
curl -X GET \\
  "${BASE}/api/v1/seizure-cases/all-cases" \\
  -H "X-Api-Key: wits_your_api_key_here"`}</Code>
            <div className="mt-4 rounded-xl border border-amber-500/15 p-4" style={{ background: 'rgba(245,158,11,0.04)' }}>
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-300 text-xs font-semibold mb-1">Keep your key secret</p>
                  <p className="text-white/35 text-xs leading-relaxed">
                    Never expose your API key in client-side JavaScript, public repositories, or URLs.
                    If a key is compromised, revoke it immediately from your dashboard and generate a new one.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ── Rate Limits ── */}
          <section id="Rate Limits">
            <SectionHead icon={<BoltIcon className="w-5 h-5" />} title="Rate Limits" />
            <p className="text-white/40 text-sm leading-relaxed mb-4">
              Rate limits protect platform stability. Exceeding the limit returns a{' '}
              <code className="text-red-400/80 bg-white/5 px-1 rounded text-xs">429 Too Many Requests</code> response.
            </p>
            <table className="w-full text-xs mb-4 border-collapse">
              <thead>
                <tr className="text-white/25 text-[10px] uppercase tracking-wider text-left border-b border-white/6">
                  <th className="pb-2 pr-6 font-medium">Access type</th>
                  <th className="pb-2 pr-6 font-medium">Limit</th>
                  <th className="pb-2 font-medium">Window</th>
                </tr>
              </thead>
              <tbody className="text-white/45">
                <tr className="border-b border-white/4">
                  <td className="py-2 pr-6">Anonymous (no key)</td>
                  <td className="py-2 pr-6">30 req / min <span className="text-white/25 text-[10px]">per IP</span></td>
                  <td className="py-2">Sliding 60s</td>
                </tr>
                <tr className="border-b border-white/4">
                  <td className="py-2 pr-6">Organisation API key (default)</td>
                  <td className="py-2 pr-6"><span className="text-emerald-400 font-semibold">60 req / min</span></td>
                  <td className="py-2">Sliding 60s</td>
                </tr>
                <tr>
                  <td className="py-2 pr-6">Enterprise / custom tier</td>
                  <td className="py-2 pr-6 text-white/25">Contact us</td>
                  <td className="py-2 text-white/25">Custom</td>
                </tr>
              </tbody>
            </table>
            <p className="text-white/30 text-xs mb-2">Rate limit headers returned on every response:</p>
            <Code>{`X-RateLimit-Limit: 60
X-RateLimit-Remaining: 57
Retry-After: 60          # only present on 429 responses`}</Code>
          </section>

          {/* ── Endpoints ── */}
          <section id="Endpoints">
            <SectionHead icon={<CodeBracketIcon className="w-5 h-5" />} title="Endpoints" />

            <div className="mb-4 flex items-center gap-3">
              <Badge color="green">Public</Badge>
              <span className="text-white/30 text-xs">No key needed</span>
              <Badge color="amber">API Key</Badge>
              <span className="text-white/30 text-xs">Requires X-Api-Key header</span>
            </div>

            <div className="flex flex-col gap-3">

              <p className="text-white/25 text-[10px] uppercase tracking-wider mt-2">Seizure Cases</p>

              <Endpoint
                method="GET"
                path="/api/v1/seizure-cases/all-cases"
                auth="none"
                summary="Returns the full list of wildlife seizure cases in the WITS database. Results are paginated. Use query parameters to filter by species, date range, or location."
                params={[
                  { name: 'page',    in: 'query', desc: 'Page index (0-based, default 0)' },
                  { name: 'size',    in: 'query', desc: 'Page size (default 20, max 100)' },
                  { name: 'species', in: 'query', desc: 'Filter by species name (partial match)' },
                ]}
                response={`{
  "content": [
    {
      "id": 142,
      "caseNumber": "WC-2024-0142",
      "species": "Elephant, Ivory",
      "location": "Mombasa, Kenya",
      "dateOfSeizure": "2024-03-15",
      "caseStatus": "Concluded",
      "judgmentOutcome": "Convicted — 5 years imprisonment",
      "latitude": -4.0435,
      "longitude": 39.6682
    }
    // ...
  ],
  "totalElements": 847,
  "totalPages": 43,
  "number": 0
}`}
                example={`curl "${BASE}/api/v1/seizure-cases/all-cases?page=0&size=20" \\
  -H "X-Api-Key: wits_your_key"`}
              />

              <Endpoint
                method="GET"
                path="/api/v1/seizure-cases/summary"
                auth="none"
                summary="Returns aggregate statistics: total cases, species breakdown, conviction rates, and year-on-year trends. Suitable for dashboard widgets."
                params={[]}
                response={`{
  "totalCases": 847,
  "concludedCases": 421,
  "ongoingCases": 312,
  "convictionRate": 78,
  "topSpecies": [
    { "species": "Elephant", "count": 214 },
    { "species": "Rhino",    "count": 97 }
  ],
  "yearTrend": [
    { "year": 2022, "count": 145 },
    { "year": 2023, "count": 198 },
    { "year": 2024, "count": 211 }
  ]
}`}
                example={`curl "${BASE}/api/v1/seizure-cases/summary"`}
              />

              <Endpoint
                method="GET"
                path="/api/v1/seizure-cases/filter"
                auth="none"
                summary="Filter seizure cases by multiple criteria simultaneously."
                params={[
                  { name: 'species',   in: 'query', desc: 'Species name (partial match)' },
                  { name: 'location',  in: 'query', desc: 'Location string (partial match)' },
                  { name: 'fromYear',  in: 'query', desc: '4-digit year — include cases from this year onwards' },
                  { name: 'toYear',    in: 'query', desc: '4-digit year — include cases up to this year' },
                  { name: 'status',    in: 'query', desc: 'ongoing | concluded | absconded' },
                ]}
                response={`[ { "id": 142, "species": "Elephant", ... } ]`}
                example={`curl "${BASE}/api/v1/seizure-cases/filter?species=elephant&fromYear=2022&status=concluded"`}
              />

              <p className="text-white/25 text-[10px] uppercase tracking-wider mt-4">Case Precedents</p>

              <Endpoint
                method="GET"
                path="/precedents/all"
                auth="none"
                summary="Returns all legal case precedents stored in the system — court rulings, sentences, and judgement summaries relevant to wildlife crime prosecution."
                params={[]}
                response={`[
  {
    "id": 18,
    "title": "Republic v Kamau — Illegal Ivory Trade",
    "court": "Nairobi High Court",
    "year": 2023,
    "outcome": "Conviction",
    "sentence": "7 years imprisonment + KES 2M fine",
    "summary": "Landmark ruling establishing minimum sentencing...",
    "species": "Elephant",
    "judge": "Hon. Justice Odhiambo",
    "reference": "HC/CR/0047/2023"
  }
]`}
                example={`curl "${BASE}/precedents/all"`}
              />

              <p className="text-white/25 text-[10px] uppercase tracking-wider mt-4">Report Snapshots (authenticated)</p>

              <Endpoint
                method="GET"
                path="/api/v1/report-snapshots"
                auth="api-key"
                summary="Returns all saved intelligence report snapshots for the authenticated user, newest first. Each snapshot includes the filter configuration and summary metrics at the time of generation."
                params={[]}
                response={`[
  {
    "id": 7,
    "title": "Post-crackdown baseline",
    "generatedAt": "2025-05-28T14:32:11",
    "speciesFilter": "all",
    "fromYear": "2022",
    "toYear": "2025",
    "namedMilestone": true,
    "metrics": {
      "total": 312,
      "convictionRate": 74,
      "topSpecies": [["Elephant", 98], ["Rhino", 54]]
    }
  }
]`}
                example={`curl "${BASE}/api/v1/report-snapshots" \\
  -H "X-Api-Key: wits_your_key"`}
              />

            </div>
          </section>

          {/* ── Response format ── */}
          <section id="Response Format">
            <SectionHead icon={<CheckCircleIcon className="w-5 h-5" />} title="Response Format" />
            <p className="text-white/40 text-sm leading-relaxed mb-4">
              All responses are JSON. List endpoints use standard Spring pagination envelopes;
              single-resource endpoints return the object directly.
            </p>
            <Code>{`// Paginated list
{
  "content":        [ /* array of items */ ],
  "totalElements":  847,
  "totalPages":     43,
  "number":         0,    // current page (0-based)
  "size":           20
}

// Single resource / mutation
{
  "message": "Operation successful.",
  "data":    { /* resource object */ }
}

// Error
{
  "error":   "Unauthorized",
  "message": "API key not found."
}`}</Code>
          </section>

          {/* ── Error codes ── */}
          <section id="Error Codes">
            <SectionHead icon={<ExclamationTriangleIcon className="w-5 h-5" />} title="Error Codes" />
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-white/25 text-[10px] uppercase tracking-wider text-left border-b border-white/6">
                  <th className="pb-2 pr-6 font-medium">Status</th>
                  <th className="pb-2 pr-6 font-medium">Meaning</th>
                  <th className="pb-2 font-medium">Common cause</th>
                </tr>
              </thead>
              <tbody className="text-white/45">
                {[
                  ['200 OK',                    'Success',              'Request completed normally'],
                  ['201 Created',               'Resource created',     'POST that creates a new record'],
                  ['400 Bad Request',           'Invalid input',        'Missing required field or malformed body'],
                  ['401 Unauthorized',          'Auth failed',          'Missing, invalid, revoked, or expired API key'],
                  ['404 Not Found',             'Resource missing',     'Unknown ID or path'],
                  ['409 Conflict',              'Duplicate',            'Attempting to create a resource that already exists'],
                  ['429 Too Many Requests',     'Rate limited',         'Exceeded per-minute request cap — wait and retry'],
                  ['500 Internal Server Error', 'Server error',         'Unexpected server-side failure — retry with backoff'],
                ].map(([code, meaning, cause]) => (
                  <tr key={code} className="border-b border-white/4">
                    <td className="py-2 pr-6 font-mono text-white/65">{code}</td>
                    <td className="py-2 pr-6">{meaning}</td>
                    <td className="py-2 text-white/30">{cause}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* ── Code examples ── */}
          <section id="Code Examples">
            <SectionHead icon={<CodeBracketIcon className="w-5 h-5" />} title="Code Examples" />

            <div className="flex flex-col gap-5">
              <div>
                <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2">cURL</p>
                <Code>{`# Fetch all elephant seizure cases, page 1
curl -X GET \\
  "${BASE}/api/v1/seizure-cases/filter?species=elephant&page=0&size=50" \\
  -H "X-Api-Key: wits_your_api_key_here" \\
  -H "Accept: application/json"`}</Code>
              </div>
              <div>
                <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2">JavaScript (fetch)</p>
                <Code>{`const API_KEY = process.env.WITS_API_KEY; // never hardcode in client code!

async function getCases(species = 'all', page = 0) {
  const params = new URLSearchParams({ page: String(page), size: '50' });
  if (species !== 'all') params.set('species', species);

  const res = await fetch(
    \`${BASE}/api/v1/seizure-cases/filter?\${params}\`,
    { headers: { 'X-Api-Key': API_KEY } }
  );

  if (res.status === 429) {
    const retryAfter = res.headers.get('Retry-After') ?? '60';
    throw new Error(\`Rate limited — retry in \${retryAfter}s\`);
  }
  if (!res.ok) throw new Error(\`WITS API error: \${res.status}\`);
  return res.json();
}

const data = await getCases('elephant');
console.log(\`\${data.totalElements} elephant cases found\`);`}</Code>
              </div>
              <div>
                <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2">Python (requests)</p>
                <Code>{`import requests, os, time

API_KEY = os.environ["WITS_API_KEY"]
BASE    = "${BASE}"

def get_summary():
    r = requests.get(f"{BASE}/api/v1/seizure-cases/summary",
                     headers={"X-Api-Key": API_KEY})
    if r.status_code == 429:
        retry = int(r.headers.get("Retry-After", 60))
        time.sleep(retry)
        return get_summary()          # simple backoff
    r.raise_for_status()
    return r.json()

summary = get_summary()
print(f"Total cases: {summary['totalCases']}")
print(f"Conviction rate: {summary['convictionRate']}%")`}</Code>
              </div>
            </div>
          </section>

          {/* Footer CTA */}
          <div className="rounded-2xl border border-green-500/15 p-8 text-center" style={{ background: 'rgba(22,163,74,0.04)' }}>
            <p className="text-white/60 text-sm font-semibold mb-1">Ready to integrate?</p>
            <p className="text-white/30 text-xs mb-4">Create a free account to generate your API key and start querying WITS data.</p>
            <Link
              to="/accounts/register"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-green-700 hover:bg-green-600 text-white text-sm font-semibold transition-colors"
            >
              Get started for free
            </Link>
          </div>

        </main>
      </div>
    </div>
  );
}

function SectionHead({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-5 pb-3 border-b border-white/6">
      <span className="text-green-400/70">{icon}</span>
      <h2 className="text-white/80 text-lg font-semibold" style={{ fontFamily: 'var(--font-display)' }}>{title}</h2>
    </div>
  );
}
